import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import { trackConnectorConnectStarted } from '@/lib/analytics/connectionTracking'
import { startGithubAppInstall } from '@/lib/auth/github'
import { Button } from '@/components/ui/shadcn/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import { DisconnectConnectionDialog } from './disconnect-connection-dialog'

// Brand-level GitHub App connection. Read-only source that feeds "what's new" (releases,
// commits, PRs) into generation. Distinct from social accounts (which we post TO).
export function GithubConnectionCard() {
  const { data: config } = trpc.brandConnection.githubConfig.useQuery()
  const { data: connections } = trpc.brandConnection.list.useQuery()
  const github = connections?.find((c) => c.provider === 'github')
  const meta = github?.metadata as
    | { accountLogin?: string; repositories?: { owner: string; name: string }[] }
    | undefined

  const handleConnect = () => {
    if (!config?.slug) {
      toast.error('GitHub is not configured for this environment')
      return
    }
    trackConnectorConnectStarted('github', !!github)
    startGithubAppInstall({ slug: config.slug, referrerPath: window.location.pathname })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">GitHub</CardTitle>
        <CardDescription>
          Releases, commits, and pull requests from your repositories. Read-only, never posted to.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
          <div className="min-w-0">
            {config?.configured === false ? (
              <p className="text-muted-foreground text-sm">Not configured for this environment.</p>
            ) : github ? (
              <p className="text-foreground truncate text-sm">
                Connected{meta?.accountLogin ? ` to ${meta.accountLogin}` : ''}
                {meta?.repositories?.length ? ` · ${meta.repositories.length} repo(s)` : ''}
              </p>
            ) : (
              <p className="text-muted-foreground text-sm">Not connected.</p>
            )}
          </div>
          {github ? (
            <div className="flex shrink-0 items-center gap-2">
              {/* Re-opens GitHub's install/configure screen. Changing repo access there
                  redirects back (Redirect-on-update) and refreshes the stored repo list. */}
              <Button
                variant="outline"
                onClick={handleConnect}
                disabled={config?.configured === false}>
                Manage repositories
              </Button>
              <DisconnectConnectionDialog provider="github">
                <Button variant="ghost">Disconnect</Button>
              </DisconnectConnectionDialog>
            </div>
          ) : (
            <Button onClick={handleConnect} disabled={config?.configured === false}>
              Connect
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
